import React, { useEffect, useState } from "react";
import { Filter, TrendingUp, Loader2, AlertCircle } from "lucide-react";
import { getAdminFunnel } from "../lib/api";
import { useI18n } from "../i18n";

/**
 * Conversion funnel + daily series visualization for the GOLDORA admin page.
 * Pure SVG bars/lines (no chart library) — keeps bundle lean.
 */
const STAGE_COLORS = {
  signup: "#E9B934",
  coach: "#F59E0B",
  checkout: "#FCD34D",
  trial: "#FBBF24",
  paying: "#34D399",
  canceled: "#71717A",
};

export default function AdminFunnel() {
  const { lang } = useI18n();
  const pt = lang === "pt";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    getAdminFunnel(days)
      .then((d) => { setData(d); setLoading(false); })
      .catch((e) => { setErr(e?.response?.data?.detail || "Failed"); setLoading(false); });
  }, [days]);

  if (loading) {
    return (
      <div className="surface p-6 mt-8" data-testid="admin-funnel-loading">
        <div className="flex items-center gap-3 text-neutral-400 font-mono text-sm uppercase tracking-[0.18em]">
          <Loader2 size={14} className="animate-spin text-gold-400" />
          {pt ? "Carregando funil…" : "Loading funnel…"}
        </div>
      </div>
    );
  }
  if (err) {
    return (
      <div className="surface p-6 mt-8" data-testid="admin-funnel-error">
        <div className="flex items-center gap-2 text-rose-400 text-sm">
          <AlertCircle size={14} /> {String(err)}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { funnel, daily_series, totals, plan_distribution } = data;
  const maxCount = Math.max(...funnel.map((s) => s.count), 1);

  // Series chart prep
  const maxSignups = Math.max(...daily_series.map((d) => d.signups), 1);
  const maxRevenue = Math.max(...daily_series.map((d) => d.revenue_usd), 1);
  const W = 100; // viewBox width
  const H = 40;  // viewBox height
  const points = (key, max) =>
    daily_series
      .map((d, i) => {
        const x = (i / Math.max(daily_series.length - 1, 1)) * W;
        const y = H - (d[key] / max) * (H - 4) - 2;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");

  return (
    <div className="mt-8 grid lg:grid-cols-12 gap-6" data-testid="admin-funnel">
      {/* Funnel */}
      <div className="lg:col-span-7 surface p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
              <Filter size={11} /> {pt ? "Funil de conversão" : "Conversion funnel"}
            </div>
            <h2 className="mt-2 font-display text-xl text-white">
              {pt ? `Últimos ${days} dias` : `Last ${days} days`}
            </h2>
          </div>
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1" data-testid="funnel-window-toggle">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                  d === days ? "bg-gold-500 text-black" : "text-neutral-400 hover:text-white"
                }`}
                data-testid={`funnel-window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-3" data-testid="funnel-stages">
          {funnel.map((s) => {
            const widthPct = Math.max((s.count / maxCount) * 100, 2);
            const color = STAGE_COLORS[s.stage] || "#E9B934";
            return (
              <div key={s.stage} data-testid={`funnel-stage-${s.stage}`}>
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <span className="text-sm text-neutral-200">{pt ? s.label_pt : s.label_en}</span>
                  <span className="font-mono text-xs text-neutral-400">
                    {s.count} <span className="text-gold-400">· {s.rate}%</span>
                  </span>
                </div>
                <div className="h-7 rounded bg-white/[0.03] border border-white/5 overflow-hidden">
                  <div
                    className="h-full transition-all duration-700 flex items-center pl-2"
                    style={{ width: `${widthPct}%`, background: `linear-gradient(90deg, ${color}66 0%, ${color} 100%)` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 border-t border-white/5" data-testid="funnel-totals">
          <FunnelStat label={pt ? "Conv. geral" : "Overall conv."} value={`${totals.conversion_rate_overall}%`} hint={pt ? "Cadastro → Pagante" : "Signup → Paying"} />
          <FunnelStat label={pt ? "Trial → Pago" : "Trial → Paid"} value={`${totals.trial_to_paid_rate}%`} hint={pt ? "Conversão pós-trial" : "Post-trial conv."} />
          <FunnelStat label={pt ? "Em trial" : "On trial"} value={totals.trials} hint={pt ? "Ativos agora" : "Active now"} tone="gold" />
          <FunnelStat label={pt ? "Pagantes" : "Paying"} value={totals.paying} hint={pt ? "Recorrentes" : "Recurring"} tone="emerald" />
        </div>
      </div>

      {/* Daily series chart */}
      <div className="lg:col-span-5 surface p-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold-400 flex items-center gap-1.5">
          <TrendingUp size={11} /> {pt ? "Atividade diária" : "Daily activity"}
        </div>
        <h2 className="mt-2 font-display text-xl text-white">
          {pt ? "Cadastros e checkouts" : "Sign-ups & checkouts"}
        </h2>

        <div className="mt-5 relative" data-testid="funnel-series-chart">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
            {/* signups line */}
            <polyline
              fill="none"
              stroke="#E9B934"
              strokeWidth="0.7"
              vectorEffect="non-scaling-stroke"
              points={points("signups", maxSignups)}
            />
            {/* checkouts line */}
            <polyline
              fill="none"
              stroke="#34D399"
              strokeWidth="0.7"
              vectorEffect="non-scaling-stroke"
              points={points("checkouts", maxSignups)}
            />
          </svg>
          <div className="mt-2 flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gold-400 inline-block" /> {pt ? "Cadastros" : "Sign-ups"}</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> {pt ? "Checkouts" : "Checkouts"}</span>
          </div>
        </div>

        {/* Plan distribution */}
        {plan_distribution && Object.keys(plan_distribution).length > 0 && (
          <div className="mt-6 pt-5 border-t border-white/5" data-testid="funnel-plan-dist">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
              {pt ? "Pagantes por plano" : "Payers by plan"}
            </div>
            <div className="mt-3 space-y-1.5">
              {Object.entries(plan_distribution).map(([k, n]) => (
                <div key={k} className="flex items-center justify-between text-sm">
                  <span className="text-neutral-300 capitalize">{k.replace("_", " ")}</span>
                  <span className="font-mono text-gold-300">{n}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MRR projected */}
        <div className="mt-6 pt-5 border-t border-white/5">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-neutral-500">
            {pt ? "Receita acumulada (janela)" : "Window revenue"}
          </div>
          <div className="mt-2 font-mono text-2xl text-emerald-300">
            ${daily_series.reduce((s, d) => s + d.revenue_usd, 0).toFixed(0)}
          </div>
          <div className="text-[10px] text-neutral-500 font-mono mt-1">
            {pt ? "Aproximação MRR (anuais normalizados / 12)" : "MRR approximation (yearly normalized / 12)"}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelStat({ label, value, hint, tone = "neutral" }) {
  const color = tone === "gold" ? "text-gold-300" : tone === "emerald" ? "text-emerald-300" : "text-white";
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`mt-1 font-mono text-xl ${color}`}>{value}</div>
      <div className="text-[10px] font-mono text-neutral-600">{hint}</div>
    </div>
  );
}
